import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import * as Ably from "ably";
import { useSelector } from "react-redux";
import { ensureAssistantIdentity } from "../utils/clientIdentity";
import { buildAssistantHeaders, buildAssistantUrl } from "../utils/assistantApi";

const AIAssistantAblyContext = createContext(null);

export const AIAssistantAblyProvider = ({ children }) => {
  const userDetail = useSelector((state) => state.userDetail);
  const [identity, setIdentity] = useState(() =>
    ensureAssistantIdentity({ userId: userDetail?.id })
  );
  const heartbeatRef = React.useRef(null);

  useEffect(() => {
    const userId = userDetail?.id;
    if (userId === undefined) return;
    const nextIdentity = ensureAssistantIdentity({ userId });
    if (nextIdentity?.clientId !== identity?.clientId) {
      setIdentity(nextIdentity);
    }
  }, [userDetail?.id, identity?.clientId]);

  useEffect(() => {
    const handleIdentityUpdate = (event) => {
      const { clientId, conversationId } = event?.detail || {};
      if (!clientId) return;
      setIdentity({ clientId, conversationId });
    };
    window.addEventListener("assistant:identity-updated", handleIdentityUpdate);
    return () => {
      window.removeEventListener("assistant:identity-updated", handleIdentityUpdate);
    };
  }, []);

  const ablyClient = useMemo(() => {
    if (!identity?.clientId) return null;
    const authUrl = `${buildAssistantUrl("ably-token/")}?clientId=${encodeURIComponent(identity.clientId)}&rnd=${Date.now()}`;
    const authHeaders = buildAssistantHeaders({ "Content-Type": undefined });
    return new Ably.Realtime({
      clientId: identity.clientId,
      closeOnUnload: false,
      authUrl,
      authHeaders,
    });
  }, [identity?.clientId]);

  useEffect(() => {
    if (!ablyClient) return;
    const handleConnectionFailed = (stateChange) => {
      console.error("[AI Assistant Ably] Connection failed", stateChange);
    };
    ablyClient.connection.on("failed", handleConnectionFailed);
    heartbeatRef.current = setInterval(async () => {
      try {
        await ablyClient.connection.ping();
      } catch (error) {
        console.warn("[AI Assistant Ably] Heartbeat failed", error);
      }
    }, 30000);
    return () => {
      ablyClient.connection.off("failed", handleConnectionFailed);
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
      ablyClient.close();
    };
  }, [ablyClient]);

  return (
    <AIAssistantAblyContext.Provider
      value={{ ablyClient, clientId: identity?.clientId, conversationId: identity?.conversationId }}
    >
      {children}
    </AIAssistantAblyContext.Provider>
  );
};

export const useAssistantAbly = () => useContext(AIAssistantAblyContext);
